import React, { useCallback, useEffect, useState, useRef } from "react";
import { createPortal } from "react-dom";
import { useParams, useLocation, useNavigate } from "react-router-dom";
import { X, Calendar, FileText } from "lucide-react";
import { ArrowLeft } from "react-feather";
import { displayToast } from "../../utils/Toast";
import { useErrorHandler } from "../../services/ErrorHandler";
import Edit from "../../assets/svgIcons/FormEdit.svg";
import Print from "../../assets/svgIcons/FormPrint.svg";
import { capitalizeFirstLetterOfEachWord } from "../../utils/Formatter";
import { getFieldMaster } from "../../apis/fieldMaster";
import {
  insertMasterTransaction,
  fetchTransactionByCode,
  fetchTransactionById,
  editMasterTransaction,
  deleteTransactionByCode,
  fetchSubcategoriesByCategoryId,
  fetchTransactionPopupData,
  fetchTransactionNo,
  fetchVendorCategoriesList,
  approveTransaction,
} from "../../apis/transaction";
import Delete from "../../assets/svgIcons/FormDelete.svg";
import ConfirmationModal from "../../components/ConfirmationModal";
import Loader from "../../components/Loader";
import { useSalesFormStoreApi } from "../../services/useSalesFormStore";
import { usePermissions } from "../../hooks/usePermissions";
import useAutoRefresh from "../../hooks/useAutoRefresh";
import { uploadFile } from "../../utils/uploadFile";
import {
  adoptAttachments,
  deleteAttachment,
  fetchAttachments,
} from "../../apis/attachments";
import { fetchDepartments } from "../../apis/tasks";
import DatePicker, { CalendarContainer } from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { useMode } from "../../context/ModeContext";
import { resolveValidationFieldUpdate } from "../../utils/validationFieldUpdate";
import "../../styles/components/MasterDataModal.css";
import "../../styles/components/TransactionTable.css";
import "../../styles/components/ConfirmationModal.css";
import "../../styles/components/PrimaryDocument.css";
import ProcessCardPopup from "../ProcessCardPopup";

const DateCalendar = ({ children, onClear, className, ...props }) => {
  const [dropdownRow, setDropdownRow] = useState(null);
  const calendarRef = useCallback((node) => {
    setDropdownRow(
      node?.querySelector(".react-datepicker__header__dropdown") ?? null,
    );
  }, []);

  return (
    <div ref={calendarRef}>
      <CalendarContainer
        {...props}
        className={`${className || ""} clearable-datepicker-calendar`}
      >
        {children}
      </CalendarContainer>
      {dropdownRow &&
        onClear &&
        createPortal(
          <button
            type="button"
            className="react-datepicker__month-read-view calendar-clear-button"
            onClick={onClear}
          >
            Clear
          </button>,
          dropdownRow,
        )}
    </div>
  );
};

const AnalysisDateInput = React.forwardRef(
  ({ value, onClick, placeholder }, ref) => (
    <div
      className="analysis-date-input-box"
      onClick={onClick}
      ref={ref}
      tabIndex={0}
    >
      <span
        className={value ? "analysis-date-value" : "analysis-date-placeholder"}
      >
        {value || placeholder}
      </span>
      <Calendar size={16} className="analysis-date-icon" />
    </div>
  ),
);

const CALENDAR_MODIFIERS = [
  {
    name: "shiftY",
    fn({ y, placement }) {
      if (String(placement).startsWith("top")) {
        return { y: Math.max(10, y) };
      }
      return {};
    },
  },
];

const DrawingStatusMaster = ({
  transactionCode: propTransactionCode,
  heading = "",
  selectedDetail = "",
  modeOfEdit = "",
  onClose,
  extraForms,
  newEntryTranCode,
  customerType = "",
  isPopupMode = false,
  isAutoTransactionNo = false,
}) => {
  const location = useLocation();
  const salesFormStore = useSalesFormStoreApi();
  const { setMode } = useMode();
  const {
    tab: urlTab,
    category,
    mode,
    transId: urlTransId,
    id: urlId,
  } = useParams();
  const navigate = useNavigate();
  const { handleErrorResponse } = useErrorHandler();
  const inputRefs = useRef({});
  const stagedAttachmentsRef = useRef([]);
  const ipmSubcategoryTriggerRef = useRef(null);
  const drawingInputRef = useRef(null);
  const qcInputRef = useRef(null);
  const hasFetchedTranNoRef = useRef(false);

  const shouldDefaultToToday = (field) => {
    const fieldName = String(field?.field_name || "").toLowerCase();
    const fieldType = String(field?.field_type || "").toUpperCase();
    const isDateType = fieldType === "DATE" || fieldType === "DATETIME";
    if (!isDateType) return false;
    if (!fieldName.includes("date")) return false;
    return field?.is_required || fieldName === "date";
  };

  const tab =
    urlTab ||
    (window.location.pathname.startsWith("/master") ? "master" : undefined);

  const transactionCode = (() => {
    if (propTransactionCode) {
      return propTransactionCode;
    }
    if (location.state?.transactionCode) {
      return location.state.transactionCode;
    }
    if (heading === "otherDetailsModal") {
      return location.state?.transactionCode;
    }
    return null;
  })();

  const showMasterApprovalWorkflow =
    transactionCode?.toUpperCase() === "VP" ||
    `${location.pathname || ""}`.toLowerCase().includes("/accounts/accounts") ||
    (`${tab || ""}`.toLowerCase().trim() === "accounts" &&
      `${category || ""}`.toLowerCase().trim() === "accounts");

  const displayCategory = (() => {
    const backendDisplayName =
      location.state?.transactionSettings?.display_name ||
      location.state?.transactionSettings?.name;

    if (backendDisplayName && String(backendDisplayName).trim() !== "") {
      return backendDisplayName;
    }

    const formattedCategory = capitalizeFirstLetterOfEachWord(
      String(category || "").replace(/-/g, " "),
    );
    return formattedCategory || "";
  })();

  const transId =
    location.state?.transId ||
    location.state?.transactionId ||
    location.state?.tran_id ||
    location.state?.id ||
    urlTransId ||
    urlId;
  const colorCode = location.state?.colorCode;
  const prefillData = location.state?.prefillData || {};
  const [formData, setFormData] = useState(prefillData);
  const [isLoading, setIsLoading] = useState(false);
  const [fields, setFields] = useState([]);
  const [extraFormsFromFetch, setExtraFormsFromFetch] = useState([]);
  const [currentMode, setCurrentMode] = useState(mode);
  useAutoRefresh(() => fetchTransactionData(), {
    enabled: currentMode === "view" && Boolean(transId),
  });
  const [dropdownOptions, setDropdownOptions] = useState({});
  const [isButtonLoading, setIsButtonLoading] = useState(false);
  const [transactionData, setTransactionData] = useState(null);
  const [openDropdownField, setOpenDropdownField] = useState(null);
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [drawingUrl, setDrawingUrl] = useState("");
  const [inspectionFormatUrl, setInspectionFormatUrl] = useState("");
  const [attachmentsByField, setAttachmentsByField] = useState({});
  const [pendingAttachmentsByField, setPendingAttachmentsByField] = useState(
    {},
  );
  const [showProcessCardPopup, setShowProcessCardPopup] = useState(false);
  const [showRejectDialog, setShowRejectDialog] = useState(false);
  const [rejectReason, setRejectReason] = useState("");
  const [actionType, setActionType] = useState("");
  const [showApproveConfirmation, setShowApproveConfirmation] = useState(false);
  const [isApprovalLoading, setIsApprovalLoading] = useState(false);
  const [showRejectReason, setShowRejectReason] = useState(false);
  const [selectedFileNames, setSelectedFileNames] = useState({});
  const attachmentParentId = formData?.tran_id || transId;
  const [categoryOptions, setCategoryOptions] = useState([]);
  const [subcategoryOptionsByCategory, setSubcategoryOptionsByCategory] =
    useState({});
  const [isCategoryLoading, setIsCategoryLoading] = useState(false);
  const [isSubcategoryLoading, setIsSubcategoryLoading] = useState(false);
  const [hasLoadedAllSubcategories, setHasLoadedAllSubcategories] =
    useState(false);
  const [ipmDropdownStyle, setIpmDropdownStyle] = useState({});
  const dynamicFileInputRefs = useRef({});
  const rights = usePermissions(transactionCode);

  const parseApiItems = (response) => {
    if (Array.isArray(response?.items)) return response.items;
    if (Array.isArray(response?.data)) return response.data;
    if (Array.isArray(response)) return response;
    return [];
  };

  const getCategoryId = (categoryOption) =>
    String(categoryOption?.id || categoryOption?.category_id || "");

  const getCategoryLabel = (categoryOption) =>
    categoryOption?.category_name ||
    categoryOption?.display_name ||
    categoryOption?.name ||
    "";

  const getSubcategoryId = (subcategoryOption) =>
    String(subcategoryOption?.id || subcategoryOption?.subcategory_id || "");

  const getSubcategoryLabel = (subcategoryOption) =>
    subcategoryOption?.subcategory_name ||
    subcategoryOption?.display_name ||
    subcategoryOption?.name ||
    "";

  const fetchIpmDisplayNameBySubcategory = async (subcategoryId) => {
    const numericSubcategoryId = Number(subcategoryId);

    if (!Number.isFinite(numericSubcategoryId) || numericSubcategoryId <= 0) {
      return "";
    }

    try {
      const response = await fetchTransactionPopupData(
        `SELECT display_name FROM 1_ipm_head WHERE subcategory_id = ${numericSubcategoryId} AND display_name IS NOT NULL AND TRIM(display_name) <> '' ORDER BY CHAR_LENGTH(display_name) DESC, tran_id DESC LIMIT 1`,
      );

      const matchedDisplayName = response?.items?.[0]?.display_name;
      return matchedDisplayName ? String(matchedDisplayName) : "";
    } catch (error) {
      console.error("Failed to fetch IPM display name:", error);
      return "";
    }
  };

  const loadAllSubcategoryOptions = async () => {
    if (hasLoadedAllSubcategories || isSubcategoryLoading) return;

    let categories = categoryOptions;
    if (categories.length === 0) {
      setIsCategoryLoading(true);
      try {
        const categoryResponse = await fetchVendorCategoriesList();
        categories = parseApiItems(categoryResponse);
        setCategoryOptions(categories);
      } catch (error) {
        displayToast("Failed to fetch categories", "error");
        setIsCategoryLoading(false);
        return;
      } finally {
        setIsCategoryLoading(false);
      }
    }

    if (!Array.isArray(categories) || categories.length === 0) return;

    setIsSubcategoryLoading(true);
    try {
      const missingCategoryIds = categories
        .map((categoryOption) => getCategoryId(categoryOption))
        .filter(
          (categoryId) =>
            categoryId && !subcategoryOptionsByCategory[categoryId],
        );

      if (missingCategoryIds.length > 0) {
        const responses = await Promise.all(
          missingCategoryIds.map((categoryId) =>
            fetchSubcategoriesByCategoryId(categoryId),
          ),
        );

        const nextSubcategoryMap = { ...subcategoryOptionsByCategory };
        missingCategoryIds.forEach((categoryId, index) => {
          nextSubcategoryMap[categoryId] = parseApiItems(responses[index]);
        });

        setSubcategoryOptionsByCategory(nextSubcategoryMap);
      }

      setHasLoadedAllSubcategories(true);
    } catch (error) {
      displayToast("Failed to fetch subcategories", "error");
    } finally {
      setIsSubcategoryLoading(false);
    }
  };

  const navigateToTransactionList = () => {
    if (tab && category) {
      sessionStorage.setItem(`clearCache_${tab}_${category}`, "true");
      navigate(`/${tab}/${category}`, {
        state: { transactionCode, forceRefresh: true },
        replace: true,
      });
    } else {
      navigate(-1);
    }
  };

  const isContractReviewMaster =
    ["CRF", "CONTRACT_REVIEW_FORM"].includes(
      String(transactionCode || "").toUpperCase(),
    ) ||
    String(category || "")
      .toLowerCase()
      .includes("contract-review-form") ||
    String(category || "")
      .toLowerCase()
      .includes("contact-review-form");

  const isTrainingAttendanceMaster =
    ["HT", "HT_TRAINING_ATTENDANCE", "TRAINING_ATTENDANCE"].includes(
      String(transactionCode || "").toUpperCase(),
    ) ||
    String(category || "")
      .toLowerCase()
      .includes("training-attendence") ||
    String(category || "")
      .toLowerCase()
      .includes("training-attendance");

  const isListOfEmployeesMaster =
    [
      "HR_LIST_OF_EMPLOYEES",
      "LIST_OF_EMPLOYEES",
      "EMPLOYEE_LIST",
      "HR_LOE",
      "LOE",
    ].includes(String(transactionCode || "").toUpperCase()) ||
    String(category || "")
      .toLowerCase()
      .includes("list-of-employees") ||
    String(category || "")
      .toLowerCase()
      .includes("list-of-employee");

  const isListOfTrainersMaster =
    [
      "HR_LIST_OF_TRAINERS",
      "LIST_OF_TRAINERS",
      "TRAINER_LIST",
      "HR_LOT",
      "LOT",
    ].includes(String(transactionCode || "").toUpperCase()) ||
    String(category || "")
      .toLowerCase()
      .includes("list-of-trainers") ||
    String(category || "")
      .toLowerCase()
      .includes("list-of-trainer");

  const isHrInductionTrainingSheetMaster =
    [
      "HR_INDUCTION_TRAINING_SHEET",
      "INDUCTION_TRAINING_SHEET",
      "HR_ITS",
      "ITS",
    ].includes(String(transactionCode || "").toUpperCase()) ||
    String(category || "")
      .toLowerCase()
      .includes("induction-training-sheet");

  const isHrTrainingNeedsRecordMaster =
    [
      "HR_TRAINING_NEEDS_RECORD",
      "TRAINING_NEEDS_RECORD",
      "HR_TNR",
      "TNR",
    ].includes(String(transactionCode || "").toUpperCase()) ||
    String(category || "")
      .toLowerCase()
      .includes("training-needs-record");

  const isHrEmployeeHistoryCardMaster =
    [
      "HR_EMPLOYEE_HISTORY_CARD",
      "EMPLOYEE_HISTORY_CARD",
      "HR_EHC",
      "EHC",
    ].includes(String(transactionCode || "").toUpperCase()) ||
    String(category || "")
      .toLowerCase()
      .includes("employee-history-card");

  const handleDynamicFileChange = async (e, fieldName) => {
    if (currentMode === "view") return;
    const files = e.target.files;
    const file = files[0];
    if (!file) return;
    try {
      const uploadResponse = await uploadFile(files, "transaction", {
        tranCode: transactionCode,
        scope: "head",
        fieldName,
        parentId: transId,
      });
      const fileUrl = uploadResponse?.url;
      if (!fileUrl) {
        displayToast("Upload failed - no URL returned", "error");
        return;
      }
      setFormData((prev) => ({ ...prev, [fieldName]: fileUrl }));
      if (!transId && uploadResponse?.attachments) {
        const uploadedAttachments = uploadResponse.attachments.map(
          (attachment) => ({ ...attachment, fieldName }),
        );
        stagedAttachmentsRef.current = [
          ...stagedAttachmentsRef.current,
          ...uploadedAttachments,
        ];
      }
      if (uploadResponse?.attachments) {
        setPendingAttachmentsByField((previous) => ({
          ...previous,
          [fieldName]: [
            ...(previous[fieldName] || []),
            ...uploadResponse.attachments,
          ],
        }));
      }
      setSelectedFileNames((prev) => ({
        ...prev,
        [fieldName]: [
          ...(Array.isArray(prev[fieldName])
            ? prev[fieldName]
            : prev[fieldName]
              ? [prev[fieldName]]
              : []),
          ...Array.from(files, (selectedFile) => selectedFile.name),
        ],
      }));
      displayToast("File uploaded successfully", "success");
    } catch (error) {
      displayToast("Failed to upload file", "error");
    }
  };

  const handleRemoveMasterAttachment = async (fieldName, attachment) => {
    const attachmentId = attachment.id ?? attachment.attachment_id;
    if (attachmentId != null) {
      try {
        await deleteAttachment(attachmentId);
      } catch (error) {
        displayToast("Failed to remove file", "error");
        return;
      }
    }

    if (attachmentId != null) {
      stagedAttachmentsRef.current = stagedAttachmentsRef.current.filter(
        (currentAttachment) =>
          (currentAttachment.id ?? currentAttachment.attachment_id) !==
          attachmentId,
      );
    }
    setFormData((previous) => ({ ...previous, [fieldName]: null }));
    setPendingAttachmentsByField((previous) => ({
      ...previous,
      [fieldName]: (previous[fieldName] || []).filter(
        (currentAttachment) =>
          (currentAttachment.id ?? currentAttachment.attachment_id) !==
          attachmentId,
      ),
    }));
    setAttachmentsByField((previous) => ({
      ...previous,
      [fieldName]: (previous[fieldName] || []).filter(
        (currentAttachment) =>
          (currentAttachment.id ?? currentAttachment.attachment_id) !==
          attachmentId,
      ),
    }));
    setSelectedFileNames((previous) => {
      const fileNames = Array.isArray(previous[fieldName])
        ? [...previous[fieldName]]
        : previous[fieldName]
          ? [previous[fieldName]]
          : [];
      const fileName = attachment.file_name || attachment.name;
      const fileIndex = fileNames.indexOf(fileName);
      if (fileIndex !== -1) {
        fileNames.splice(fileIndex, 1);
      }
      return { ...previous, [fieldName]: fileNames };
    });
  };

  const resolveSavedMasterTransactionId = async (response) => {
    const responseTransactionId =
      response?.tran_id ||
      response?.head_data?.tran_id ||
      response?.data?.tran_id ||
      response?.data?.head_data?.tran_id;
    if (responseTransactionId) return responseTransactionId;

    const transactionNumber =
      response?.tran_no ||
      response?.head_data?.tran_no ||
      response?.data?.tran_no ||
      response?.data?.head_data?.tran_no;
    if (!transactionNumber) return null;

    const transactionListResponse = await fetchTransactionByCode({
      transactionCode,
      page: 1,
      pageSize: 1000,
    });
    const transactionList =
      transactionListResponse?.TransactionList ||
      transactionListResponse?.items ||
      transactionListResponse?.data?.TransactionList ||
      [];
    const savedTransaction = transactionList.find(
      (transaction) =>
        String(transaction.tran_no || "").trim() ===
        String(transactionNumber).trim(),
    );

    return savedTransaction?.tran_id || savedTransaction?.id || null;
  };

  useEffect(() => {
    if (
      (currentMode !== "view" && currentMode !== "edit") ||
      !attachmentParentId ||
      !transactionCode
    ) {
      setAttachmentsByField({});
      return;
    }

    const loadAttachments = async () => {
      try {
        const response = await fetchAttachments({
          tranCode: transactionCode,
          scope: "head",
          parentId: attachmentParentId,
        });
        const attachments = Array.isArray(response)
          ? response
          : response?.attachments || response?.items || response?.data || [];
        setAttachmentsByField(
          attachments.reduce((groups, attachment) => {
            if (attachment.field_name) {
              groups[attachment.field_name] = [
                ...(groups[attachment.field_name] || []),
                attachment,
              ];
            }
            return groups;
          }, {}),
        );
      } catch (error) {
        console.error("Failed to load master attachments:", error);
        setAttachmentsByField({});
      }
    };

    loadAttachments();
  }, [attachmentParentId, currentMode, transactionCode]);

  const handleRemoveDynamicFile = (fieldName) => {
    setFormData((prev) => ({ ...prev, [fieldName]: "" }));
    setSelectedFileNames((prev) => {
      const next = { ...prev };
      delete next[fieldName];
      return next;
    });
    if (dynamicFileInputRefs.current[fieldName]) {
      dynamicFileInputRefs.current[fieldName].value = "";
    }
  };

  const handleFileDownload = async (url, fileName) => {
    try {
      const response = await fetch(url);
      const blob = await response.blob();
      const objectUrl = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = objectUrl;
      link.download = fileName || url.split("/").pop() || "download";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      // Delay revoking URL to allow download to complete
      setTimeout(() => URL.revokeObjectURL(objectUrl), 100);
    } catch (error) {
      window.open(url, "_blank");
    }
  };

  const handleFileUpload = async (file, type) => {
    if (!file) return;

    try {
      const uploadedUrl = await uploadFile(file);
      if (type === "drawing") {
        setDrawingUrl(uploadedUrl);
        setFormData((prev) => ({ ...prev, drawing_url: uploadedUrl }));
      } else if (type === "inspection_format") {
        setInspectionFormatUrl(uploadedUrl);
        setFormData((prev) => ({
          ...prev,
          inspection_format_url: uploadedUrl,
        }));
      }
      displayToast(
        `${
          type === "drawing" ? "Drawing" : "Inspection Format"
        } uploaded successfully`,
        "success",
      );
    } catch (error) {
      displayToast(
        `Failed to upload ${
          type === "drawing" ? "drawing" : "inspection format"
        }`,
        "error",
      );
    }
  };

  const handleProcessCardSaved = () => {
    if (typeof fetchTransactionData === "function") {
      fetchTransactionData();
    } else {
      window.location.reload();
    }
  };

  const fetchFieldMaster = async () => {
    if (isLoading) return;
    setIsLoading(true);
    try {
      const response = await getFieldMaster({
        transactionCode: transactionCode,
      });
      if (response.description) {
        handleErrorResponse(response.description);
        displayToast(response.description, "error");
      } else {
        let validFields = (response.items?.head_fields || [])
          .filter((field) => !field.is_internal_field)
          .sort((a, b) => a.order_no - b.order_no);

        if (isPopupMode) {
          validFields = validFields.filter((field) => field.is_required);
        }

        setFields(validFields);
        setExtraFormsFromFetch(response.items?.extra_forms || []);

        if (!transId && mode !== "view") {
          const initialFormData = validFields.reduce((acc, field) => {
            const fieldType = String(field?.field_type || "").toUpperCase();
            const isDateType = fieldType === "DATE" || fieldType === "DATETIME";
            if (isDateType) {
              acc[field.field_name] = shouldDefaultToToday(field)
                ? new Date().toISOString().split("T")[0]
                : "";
            } else {
              acc[field.field_name] = "";
            }
            return acc;
          }, {});
          if (validFields.some((f) => f.field_name === "type")) {
            initialFormData.type = "Engineering";
          }
          setFormData((prev) => ({ ...initialFormData, ...prev }));
        }
      }
    } catch (error) {
      displayToast("Failed to fetch field master data", "error");
    } finally {
      setIsLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    if (value === "Add New +") {
      return;
    }
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleEnterValidation = async (field) => {
    if (!field?.enter_validation) return;

    if (field.enter_validation.is_db_query && field.enter_validation.db_query) {
      try {
        const response = await fetchTransactionPopupData(
          field.enter_validation.db_query,
        );

        if (response.items && field.enter_validation.field_updation) {
          const updatedFields = {};

          field.enter_validation.field_updation.forEach((update) => {
            const { copy_to_table_type } = update;

            if (copy_to_table_type === "head") {
              const resolvedUpdate = resolveValidationFieldUpdate(update, {
                db: response.items?.[0] || {},
                head: formData || {},
              });

              if (resolvedUpdate?.targetField) {
                updatedFields[resolvedUpdate.targetField] =
                  resolvedUpdate.value;
              }
            }
          });

          if (Object.keys(updatedFields).length > 0) {
            setFormData((prev) => ({
              ...prev,
              ...updatedFields,
            }));
          }
        }
      } catch (error) {
        console.error("Enter Validation Error:", error);
      }
    }
  };

  const handleLeaveValidation = async (field) => {
    if (!field?.leave_validation) return;

    if (field.leave_validation.is_db_query && field.leave_validation.db_query) {
      try {
        const response = await fetchTransactionPopupData(
          field.leave_validation.db_query,
        );

        if (response.items && field.leave_validation.field_updation) {
          const updatedFields = {};

          field.leave_validation.field_updation.forEach((update) => {
            const { copy_to_table_type } = update;

            if (copy_to_table_type === "head") {
              const resolvedUpdate = resolveValidationFieldUpdate(update, {
                db: response.items?.[0] || {},
                head: formData || {},
              });

              if (resolvedUpdate?.targetField) {
                updatedFields[resolvedUpdate.targetField] =
                  resolvedUpdate.value;
              }
            }
          });

          if (Object.keys(updatedFields).length > 0) {
            setFormData((prev) => ({
              ...prev,
              ...updatedFields,
            }));
          }
        }
      } catch (error) {
        console.error("Leave Validation Error:", error);
      }
    }
  };

  const withTransactionDate = (headData) => {
    const normalized = { ...headData };
    const dateFieldNames = (fields || [])
      .filter((field) =>
        ["DATE", "DATETIME"].includes(
          String(field?.field_type || "").toUpperCase(),
        ),
      )
      .map((field) => field.field_name);

    dateFieldNames.forEach((name) => {
      const value = normalized[name];
      if (value === "" || value === null || value === undefined) {
        delete normalized[name];
      }
    });

    if (!normalized.date) {
      const firstFilledDate = dateFieldNames
        .map((name) => normalized[name])
        .find((value) => value);
      normalized.date =
        String(firstFilledDate || "").split("T")[0] ||
        new Date().toISOString().split("T")[0];
    }

    return normalized;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsButtonLoading(true);

    if ((currentMode || modeOfEdit) === "add" && !rights.can_add) {
      displayToast(
        `You dont have permission to add ${displayCategory}`,
        "error",
      );
      setIsButtonLoading(false);
      return;
    }

    if (currentMode === "edit" && !rights.can_edit) {
      displayToast(
        `You dont have permission to edit ${displayCategory}`,
        "error",
      );
      setIsButtonLoading(false);
      return;
    }

    const { addOtherDetails, otherDetailsData } = salesFormStore.getState();

    if (heading === "otherDetailsModal" && modeOfEdit === "add") {
      let detailsMap = new Map();

      otherDetailsData.forEach((item) => {
        const key = Object.keys(item)[0];
        detailsMap.set(key, item);
      });

      Object.keys(formData).forEach((key) => {
        detailsMap.set(key, { [key]: formData[key] });
      });

      const updatedDetails = Array.from(detailsMap.values());

      addOtherDetails(updatedDetails);
      displayToast("Other details saved successfully", "success");
      onClose();
      setIsButtonLoading(false);
      return;
    }

    const isAddMode = (currentMode || modeOfEdit) === "add";

    if (isAddMode) {
      const requiredFields = fields.filter((field) => field.is_required);
      const missingRequiredFields = requiredFields.filter((field) => {
        const value = formData[field.field_name];
        return !value || value.toString().trim() === "";
      });

      if (missingRequiredFields.length > 0) {
        const missingFieldNames = missingRequiredFields
          .map((field) => field.title)
          .join(", ");
        displayToast(
          `Please fill in the required fields: ${missingFieldNames}`,
          "error",
        );
        setIsButtonLoading(false);
        return;
      }

      const hasAnyData = Object.values(formData).some(
        (value) => value && value.toString().trim() !== "",
      );

      if (!hasAnyData) {
        displayToast("Please enter at least one field before saving", "error");
        setIsButtonLoading(false);
        return;
      }
    }

    if (isAddMode) {
      const organisation = JSON.parse(localStorage.getItem("organisation"));
      const organisationId = organisation?.id;
      if (!organisationId) {
        displayToast("Organisation ID not found", "error");
        setIsButtonLoading(false);
        return;
      }
      const allowedHeadFieldNames = new Set(
        (fields || []).map((f) => f.field_name),
      );
      const filteredFormData = Object.keys(formData || {})
        .filter((key) => allowedHeadFieldNames.has(key))
        .reduce((acc, key) => {
          acc[key] = formData[key];
          return acc;
        }, {});

      const headData = {
        tran_code: transactionCode,
        tran_no: "",
        ...filteredFormData,
      };
      if (allowedHeadFieldNames.has("organisation_id")) {
        headData.organisation_id = organisationId;
      }

      if (allowedHeadFieldNames.has("drawing_url") && drawingUrl) {
        headData.drawing_url = drawingUrl;
      }
      if (
        allowedHeadFieldNames.has("inspection_format_url") &&
        inspectionFormatUrl
      ) {
        headData.inspection_format_url = inspectionFormatUrl;
      }

      const submissionData = { head_data: withTransactionDate(headData) };
      if (location.state?.sourceTranCode && location.state?.sourceTranId) {
        submissionData.source_tran_code = location.state.sourceTranCode;
        submissionData.source_tran_id = Number(location.state.sourceTranId);
      }
      if (submissionData.head_data["oraganization _id"]) {
        submissionData.head_data.organisation_id =
          submissionData.head_data.organisation_id ||
          submissionData.head_data["oraganization _id"];
        delete submissionData.head_data["oraganization _id"];
      }
      if (submissionData.head_data.oraganization_id) {
        submissionData.head_data.organisation_id =
          submissionData.head_data.organisation_id ||
          submissionData.head_data.oraganization_id;
        delete submissionData.head_data.oraganization_id;
      }

      try {
        const response = await insertMasterTransaction(
          submissionData,
          transactionCode,
        );
        const savedParentId = await resolveSavedMasterTransactionId(response);
        const attachmentIds = stagedAttachmentsRef.current
          .map((attachment) => attachment.id ?? attachment.attachment_id)
          .filter((attachmentId) => attachmentId != null);

        if (attachmentIds.length) {
          if (!savedParentId) {
            displayToast(
              "Transaction was saved, but uploaded files could not be linked",
              "error",
            );
          } else {
            await adoptAttachments({
              attachmentIds,
              tranCode: transactionCode,
              scope: "head",
              parentId: savedParentId,
            });
            stagedAttachmentsRef.current = [];
          }
        }

        const isSaveSuccessful = Boolean(
          savedParentId || response?.head_data || response?.data?.head_data,
        );

        if (isSaveSuccessful) {
          displayToast("Transaction added successfully", "success");

          if (
            location.state?.sourceTranCode &&
            location.state?.sourceTranId &&
            location.state?.sourceCategory &&
            tab
          ) {
            sessionStorage.setItem(
              `clearCache_${tab}_${location.state.sourceCategory}`,
              "true",
            );
          }

          if (isPopupMode) {
            onClose();
          } else {
            navigateToTransactionList();
          }
        } else if (response?.description) {
          handleErrorResponse(response.description);
          displayToast(response.description, "error");
        }
      } catch (error) {
        displayToast("Error adding transaction", "error");
      } finally {
        setIsButtonLoading(false);
      }
    } else if (currentMode === "edit" && transId) {
      try {
        const allowedHeadFieldNames = new Set(
          (fields || []).map((f) => f.field_name),
        );
        const filteredFormData = Object.keys(formData || {})
          .filter((key) => allowedHeadFieldNames.has(key))
          .reduce((acc, key) => {
            acc[key] = formData[key];
            return acc;
          }, {});

        const headData = {
          tran_code: transactionCode,
          ...filteredFormData,
        };
        if (allowedHeadFieldNames.has("drawing_url") && drawingUrl) {
          headData.drawing_url = drawingUrl;
        }
        if (
          allowedHeadFieldNames.has("inspection_format_url") &&
          inspectionFormatUrl
        ) {
          headData.inspection_format_url = inspectionFormatUrl;
        }

        const updatedData = {
          tran_id: transId,
          head_data: withTransactionDate(headData),
          item_data: [],
        };

        const response = await editMasterTransaction(
          transactionCode,
          updatedData,
        );

        if (response?.head_data) {
          displayToast("Transaction updated successfully", "success");
          navigateToTransactionList();
        } else if (response?.description) {
          handleErrorResponse(response.description);
          displayToast(response.description, "error");
        }
      } catch (error) {
        displayToast("Error updating transaction", "error");
      } finally {
        setIsButtonLoading(false);
      }
    }
  };

  const toggleEditMode = () => {
    if (currentMode === "view") {
      setCurrentMode("edit");
      setMode("edit");
    }
  };

  const handleCloseEditMode = () => {
    setCurrentMode("view");
    setMode("view");
  };

  const handleDeleteClick = () => {
    if (rights.can_delete) {
      setShowDeleteConfirmation(true);
    }
  };

  const handleDeleteConfirm = async () => {
    try {
      const response = await deleteTransactionByCode(transactionCode, transId);
      if (response?.description) {
        displayToast(response.description, "error");
      } else {
        displayToast("Transaction deleted successfully", "success");
        navigateToTransactionList();
      }
    } catch (error) {
      displayToast("Error deleting transaction", "error");
    }
    setShowDeleteConfirmation(false);
  };

  const handlePrintClick = async () => {
    if (!formData || Object.keys(formData).length === 0) {
      displayToast("No transaction data available to print", "error");
      return;
    }

    let latestTransactionData = transactionData;

    if (
      (!latestTransactionData || !latestTransactionData.head_data) &&
      transId
    ) {
      try {
        latestTransactionData = await fetchTransactionById(
          transactionCode,
          transId,
        );
      } catch (error) {
        displayToast(
          "Failed to load latest transaction data for print",
          "error",
        );
        return;
      }
    }

    const fileName = location.state?.documentFileName || "invoice";

    navigate(`/${tab}/${category}/transactions/${fileName}`, {
      state: {
        formData: {
          headDetails: latestTransactionData?.head_data || formData,
          itemDetails: latestTransactionData?.item_data || [],
          companyData: latestTransactionData?.company_data || {},
          rowData: [],
          transactionCode,
        },
      },
    });
  };

  const handleApprove = () => {
    setShowApproveConfirmation(true);
  };

  const handleHold = () => {
    setRejectReason("");
    setActionType("hold");
    setShowRejectDialog(true);
  };

  const handleConfirmApprove = async () => {
    try {
      setIsApprovalLoading(true);
      const submissionData = {
        tran_id: transId,
        approve_status: "approved",
      };

      const response = await approveTransaction(
        submissionData,
        transactionCode,
      );

      if (response.description) {
        handleErrorResponse(response.description);
        displayToast(response.description, "error");
      } else {
        displayToast("Transaction approved successfully", "success");
        navigateToTransactionList();
      }
    } catch (error) {
      console.error("Error approving transaction:", error);
      displayToast("Failed to approve transaction", "error");
    } finally {
      setIsApprovalLoading(false);
      setShowApproveConfirmation(false);
    }
  };

  const handleReject = () => {
    setRejectReason("");
    setActionType("rejected");
    setShowRejectDialog(true);
  };

  const handleConfirmRejectOrHold = async () => {
    try {
      setIsApprovalLoading(true);
      const submissionData = {
        tran_id: transId,
        approve_status: actionType,
        reject_reason: rejectReason,
      };
      const response = await approveTransaction(
        submissionData,
        transactionCode,
      );

      if (response.description) {
        handleErrorResponse(response.description);
        displayToast(response.description, "error");
      } else {
        setFormData((prev) => ({
          ...prev,
          approve_status: actionType,
          reject_reason: rejectReason,
        }));

        displayToast(
          actionType === "hold"
            ? "Transaction moved to hold successfully"
            : "Transaction rejected successfully",
          "success",
        );

        navigateToTransactionList();
      }
    } catch (error) {
      console.error("Error updating transaction status:", error);
      displayToast("Failed to update transaction status", "error");
    } finally {
      setIsApprovalLoading(false);
      setShowRejectDialog(false);
      setRejectReason("");
      setActionType("");
    }
  };

  const fetchTransactionData = async () => {
    if (!transId) return;
    try {
      const response = await fetchTransactionById(transactionCode, transId);
      if (response?.head_data) {
        const head = response.head_data || {};
        const company = response.company_data || {};
        setFormData({
          ...head,
          company_name: head.company_name || company.company_name || "",
        });
        setTransactionData(response);
        if (response.head_data.drawing_url) {
          setDrawingUrl(response.head_data.drawing_url);
        }
        if (response.head_data.inspection_format_url) {
          setInspectionFormatUrl(response.head_data.inspection_format_url);
        }
        setCurrentMode("view");
        setMode("view");
      }
    } catch (error) {
      displayToast("Error fetching transaction data", "error");
    }
  };
  useEffect(() => {
    if (transId && !currentMode) {
      setCurrentMode("view");
      setMode("view");
    }
  }, [transId, currentMode, setMode]);

  useEffect(() => {
    if (transId && !transactionData) {
      fetchTransactionData();
    }
  }, [transId, transactionData]);

  useEffect(() => {
    fetchFieldMaster();
  }, [transactionCode]);

  useEffect(() => {
    if (showApproveConfirmation || showRejectDialog) {
      setIsApprovalLoading(false);
    }
  }, [showApproveConfirmation, showRejectDialog]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (!e.target.closest(".modal-dropdown-container")) {
        setOpenDropdownField(null);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    const extractOptionLabel = (item) => {
      if (item == null) return "";
      if (typeof item === "string" || typeof item === "number")
        return String(item);
      const candidateKeys = [
        "name",
        "value",
        "title",
        "label",
        "category",
        "type",
        "description",
      ];
      for (const key of candidateKeys) {
        if (item[key] != null && item[key] !== "") return String(item[key]);
      }
      const firstKey = Object.keys(item)[0];
      return firstKey ? String(item[firstKey]) : "";
    };

    const normalizeStaticValues = (values) => {
      if (!values) return [];
      if (Array.isArray(values)) return values.map((v) => String(v));
      if (typeof values === "string")
        return values
          .split(",")
          .map((v) => v.trim())
          .filter((v) => v.length > 0);
      return [];
    };

    const fetchAllDropdownOptions = async () => {
      if (!Array.isArray(fields) || fields.length === 0) return;
      const newOptions = {};

      for (const field of fields) {
        const isDropdownType =
          field.field_type === "DROPDOWN" ||
          field.field_type === "dropdown" ||
          field.field_type === "DROP_DOWN" ||
          field.data_type === "DROPDOWN";

        if (!isDropdownType) continue;
        if (field.is_query && field.db_query) {
          try {
            const response = await fetchTransactionPopupData(field.db_query);
            if (response?.items?.length > 0) {
              newOptions[field.field_name] = response.items
                .map(extractOptionLabel)
                .filter((x) => x !== "");
            } else if (response?.message) {
              displayToast(response.message, "error");
            }
          } catch (error) {
            console.error(
              `Error fetching options for ${field.field_name}:`,
              error,
            );
          }
          continue;
        }
        const staticValues = normalizeStaticValues(
          field.dropdown_values || field.dropdownValues || field.options,
        );
        if (staticValues.length > 0) {
          newOptions[field.field_name] = staticValues;
        }
      }

      if (Object.keys(newOptions).length > 0) {
        setDropdownOptions((prev) => ({ ...prev, ...newOptions }));
      }
    };

    fetchAllDropdownOptions();
  }, [fields]);

  useEffect(() => {
    if (!isListOfEmployeesMaster) return;
    const designationField = (fields || []).find(
      (field) => field.field_name === "designation",
    );
    if (!designationField) return;
    const configuredOptions =
      designationField.dropdown_values ||
      designationField.dropdownValues ||
      designationField.options;
    const hasConfiguredOptions = Array.isArray(configuredOptions)
      ? configuredOptions.some((option) => String(option).trim())
      : typeof configuredOptions === "string" &&
        configuredOptions.split(",").some((option) => option.trim());
    if (
      hasConfiguredOptions ||
      (designationField.is_query && designationField.db_query)
    ) {
      return;
    }

    let isActive = true;
    const loadDepartmentOptions = async () => {
      try {
        const response = await fetchDepartments();
        const departments = Array.isArray(response)
          ? response
          : response?.items || response?.departments || [];
        const names = [
          ...new Set(
            departments
              .filter(
                (department) =>
                  String(department?.status || "active").toLowerCase() !==
                  "inactive",
              )
              .map((department) => String(department?.name || "").trim())
              .filter(Boolean),
          ),
        ];
        if (isActive && names.length > 0) {
          setDropdownOptions((prev) => ({ ...prev, designation: names }));
        }
      } catch (error) {
        console.error("Failed to fetch departments for designation:", error);
      }
    };

    loadDepartmentOptions();
    return () => {
      isActive = false;
    };
  }, [fields, isListOfEmployeesMaster]);

  useEffect(() => {
    const shouldFetchTranNo =
      (mode === "add" || currentMode === "add" || modeOfEdit === "add") &&
      !formData?.tran_no &&
      !!transactionCode;

    if (!shouldFetchTranNo) return;
    if (hasFetchedTranNoRef.current) return;

    hasFetchedTranNoRef.current = true;
    const getTransactionNumber = async () => {
      try {
        const response = await fetchTransactionNo(transactionCode);
        const transactionNo = response?.TransactionNumber;
        if (transactionNo) {
          setFormData((prev) => ({ ...prev, tran_no: transactionNo }));
        }
      } catch (error) {
        console.error("Failed to fetch transaction number:", error);
        hasFetchedTranNoRef.current = false;
      }
    };

    getTransactionNumber();
  }, [transactionCode, mode, currentMode, modeOfEdit, formData?.tran_no]);

  useEffect(() => {
    if (formData?.subcategory_id && !hasLoadedAllSubcategories) {
      loadAllSubcategoryOptions();
    }
  }, [formData?.subcategory_id, hasLoadedAllSubcategories]);

  useEffect(() => {
    const isIpmSubcategoryOpen =
      String(transactionCode || "").toUpperCase() === "IPM" &&
      openDropdownField === "subcategory_id";

    if (!isIpmSubcategoryOpen) return;

    const updateIpmDropdownPosition = () => {
      const trigger = ipmSubcategoryTriggerRef.current;
      if (!trigger) return;

      const rect = trigger.getBoundingClientRect();
      const viewportBottomPadding = 16;
      const availableBelow = Math.max(
        140,
        window.innerHeight - rect.bottom - viewportBottomPadding,
      );
      const computedMaxHeight = Math.min(220, availableBelow);

      setIpmDropdownStyle({
        position: "fixed",
        top: rect.bottom + 6,
        left: rect.left,
        width: rect.width,
        zIndex: 4000,
        maxHeight: computedMaxHeight,
      });
    };

    updateIpmDropdownPosition();
    window.addEventListener("resize", updateIpmDropdownPosition);
    window.addEventListener("scroll", updateIpmDropdownPosition, true);

    return () => {
      window.removeEventListener("resize", updateIpmDropdownPosition);
      window.removeEventListener("scroll", updateIpmDropdownPosition, true);
    };
  }, [openDropdownField, transactionCode]);

  const renderField = (field) => {
    if (!field) return null;

    const fieldValue = formData[field.field_name] || "";
    const optionsToRender = dropdownOptions[field.field_name] || [];
    const isTranNoField = field.field_name === "tran_no";
    const isEmployeeMobileNumberField =
      isListOfEmployeesMaster &&
      ["mobile", "mobile_no", "mobile_number"].includes(
        String(field.field_name || "")
          .trim()
          .toLowerCase(),
      );

    const isEmployeeDesignationField =
      isListOfEmployeesMaster && field.field_name === "designation";

    const isDropdownType =
      field.field_type === "DROPDOWN" ||
      field.field_type === "dropdown" ||
      field.field_type === "DROP_DOWN" ||
      field.data_type === "DROPDOWN" ||
      isEmployeeDesignationField;

    const isFileUploadField =
      field.field_type === "file_upload" || field.field_type === "FILE_UPLOAD";
    const fieldAttachments = attachmentsByField[field.field_name] || [];
    const savedAttachmentIds = new Set(
      fieldAttachments
        .map((attachment) => attachment.id ?? attachment.attachment_id)
        .filter((attachmentId) => attachmentId != null),
    );
    const pendingFieldAttachments = [
      ...fieldAttachments,
      ...(pendingAttachmentsByField[field.field_name] || []).filter(
        (attachment) => {
          const attachmentId = attachment.id ?? attachment.attachment_id;
          return attachmentId == null || !savedAttachmentIds.has(attachmentId);
        },
      ),
    ];
    const selectedFieldFileNames = Array.isArray(
      selectedFileNames[field.field_name],
    )
      ? selectedFileNames[field.field_name]
      : selectedFileNames[field.field_name]
        ? [selectedFileNames[field.field_name]]
        : [];

    // Only long text fields are multi-line; standard VARCHAR(255) fields
    // (email, website, contact, etc.) stay single-line so grid rows stay aligned.
    const isMultiLineField =
      !isFileUploadField &&
      (field.field_type?.toUpperCase() === "TEXT" ||
        (field.field_length &&
          field.field_length > 255 &&
          !field.options &&
          !field.is_query));

    const isInventorySubcategoryField =
      String(transactionCode || "").toUpperCase() === "IPM" &&
      String(field.field_name || "")
        .trim()
        .toLowerCase() === "subcategory_id";

    const getSubcategoryNameById = (subcategoryId) => {
      if (!subcategoryId) return "";

      for (const subcategories of Object.values(subcategoryOptionsByCategory)) {
        const matchedSubcategory = (subcategories || []).find(
          (subcategoryOption) =>
            getSubcategoryId(subcategoryOption) === String(subcategoryId),
        );
        if (matchedSubcategory) {
          return getSubcategoryLabel(matchedSubcategory);
        }
      }

      return "";
    };

    const adjustHeight = (textarea) => {
      if (textarea) {
        textarea.style.height = "24px";
        const newHeight = Math.min(textarea.scrollHeight, 72);
        textarea.style.height = `${newHeight}px`;
        textarea.style.overflowY =
          textarea.scrollHeight > 72 ? "auto" : "hidden";
      }
    };

    return (
      <div
        key={field.field_name}
        className="master-data-modal-form-field"
        onClick={
          currentMode !== "view"
            ? () => handleEnterValidation(field)
            : undefined
        }
        onBlur={
          currentMode !== "view"
            ? () => handleLeaveValidation(field)
            : undefined
        }
      >
        <label className="master-data-modal-label">
          {field.title}
          {field.is_required && <span className="required-asterisk">*</span>}
        </label>

        {isDropdownType ? (
          <div className="modal-dropdown-container">
            <div
              className={`master-data-modal-select-wrapper ${
                openDropdownField === field.field_name
                  ? "dropdown-open-highlight"
                  : ""
              }`}
              onClick={() => {
                if (currentMode !== "view") {
                  setOpenDropdownField(
                    openDropdownField === field.field_name
                      ? null
                      : field.field_name,
                  );
                }
              }}
              style={{
                cursor: currentMode === "view" ? "not-allowed" : "pointer",
                background:
                  currentMode === "view" ? "var(--surface-2)" : undefined,
              }}
            >
              <span className="modal-dropdown-text">
                {fieldValue || `Select ${field.title}`}
              </span>
              <span className="dropdown-icon">▼</span>
            </div>

            {openDropdownField === field.field_name &&
              currentMode !== "view" && (
                <div className="primary-dropdown-options">
                  {Array.isArray(optionsToRender) &&
                    optionsToRender.map((option, index) => {
                      const isSelected = formData[field.field_name] === option;
                      return (
                        <div
                          key={index}
                          className={`primary-dropdown-option ${
                            isSelected ? "selected-option" : ""
                          }`}
                          onClick={() => {
                            handleInputChange({
                              target: {
                                name: field.field_name,
                                value: option,
                              },
                            });
                            setOpenDropdownField(null);
                          }}
                        >
                          {option}
                        </div>
                      );
                    })}
                </div>
              )}
          </div>
        ) : isInventorySubcategoryField ? (
          <div
            className="modal-dropdown-container"
            style={{ position: "relative" }}
          >
            <div
              ref={ipmSubcategoryTriggerRef}
              className={`master-data-modal-select-wrapper ${
                openDropdownField === field.field_name
                  ? "dropdown-open-highlight"
                  : ""
              }`}
              onClick={async () => {
                if (currentMode === "view") return;
                await loadAllSubcategoryOptions();
                setOpenDropdownField(
                  openDropdownField === field.field_name
                    ? null
                    : field.field_name,
                );
              }}
              style={{
                cursor: currentMode === "view" ? "not-allowed" : "pointer",
                background:
                  currentMode === "view" ? "var(--surface-2)" : undefined,
              }}
            >
              <span className="modal-dropdown-text">
                {getSubcategoryNameById(fieldValue) || "Select Subcategory"}
              </span>
              <span className="dropdown-icon">▼</span>
            </div>

            {openDropdownField === field.field_name &&
              currentMode !== "view" && (
                <div
                  className="primary-dropdown-options"
                  style={{
                    ...ipmDropdownStyle,
                    border: "1px solid var(--border-soft)",
                    borderTop: "1px solid var(--border-soft)",
                    borderRadius: 10,
                    boxShadow: "0 12px 24px rgba(15, 23, 42, 0.12)",
                    background: "var(--surface)",
                    overflowY: "auto",
                    overflowX: "hidden",
                  }}
                >
                  {(isCategoryLoading || isSubcategoryLoading) && (
                    <div className="primary-dropdown-option">Loading...</div>
                  )}

                  {!isCategoryLoading &&
                    !isSubcategoryLoading &&
                    categoryOptions.map((categoryOption) => {
                      const categoryId = getCategoryId(categoryOption);
                      const categoryLabel = getCategoryLabel(categoryOption);
                      const subcategories =
                        subcategoryOptionsByCategory[categoryId] || [];

                      if (!categoryId || subcategories.length === 0) {
                        return null;
                      }

                      return (
                        <div key={categoryId}>
                          <div
                            style={{
                              padding: "6px 10px 4px",
                              fontSize: 11,
                              fontWeight: 700,
                              letterSpacing: "0.04em",
                              textTransform: "uppercase",
                              color: "var(--text-muted)",
                              background: "var(--surface-2)",
                              borderBottom: "1px solid var(--border-soft)",
                            }}
                          >
                            {categoryLabel}
                          </div>
                          {subcategories.map((subcategoryOption) => {
                            const optionId =
                              getSubcategoryId(subcategoryOption);
                            const optionLabel =
                              getSubcategoryLabel(subcategoryOption);
                            const isSelected =
                              String(fieldValue || "") === optionId;

                            return (
                              <div
                                key={optionId}
                                className={`primary-dropdown-option ${
                                  isSelected ? "selected-option" : ""
                                }`}
                                style={{
                                  padding: "7px 10px 7px 14px",
                                  borderBottom: "1px solid var(--border-soft)",
                                  fontSize: 12,
                                }}
                                onClick={async () => {
                                  const backendDisplayName =
                                    await fetchIpmDisplayNameBySubcategory(
                                      optionId,
                                    );

                                  setFormData((prev) => ({
                                    ...prev,
                                    [field.field_name]: optionId,
                                    display_name:
                                      backendDisplayName ||
                                      subcategoryOption?.display_name ||
                                      optionLabel,
                                  }));
                                  setOpenDropdownField(null);
                                }}
                              >
                                {optionLabel}
                              </div>
                            );
                          })}
                        </div>
                      );
                    })}
                </div>
              )}
          </div>
        ) : field.field_type === "BOOLEAN" ? (
          <input
            type="checkbox"
            name={field.field_name}
            className="master-data-modal-checkbox"
            checked={!!fieldValue}
            onChange={(e) =>
              handleInputChange({
                target: {
                  name: field.field_name,
                  value: e.target.checked,
                },
              })
            }
            disabled={currentMode === "view"}
          />
        ) : ["DATE", "DATETIME"].includes(
            String(field.field_type || "").toUpperCase(),
          ) ? (
          <DatePicker
            selected={(() => {
              if (!fieldValue) {
                return null;
              }
              const date = new Date(fieldValue);
              if (Number.isNaN(date.getTime()) || date.getFullYear() === 1) {
                return null;
              }
              return date;
            })()}
            onChange={(date) =>
              handleInputChange({
                target: {
                  name: field.field_name,
                  value: date ? date.toISOString().split("T")[0] : "",
                },
              })
            }
            dateFormat="dd-MM-yyyy"
            calendarContainer={(props) => (
              <DateCalendar
                {...props}
                onClear={
                  currentMode === "view"
                    ? undefined
                    : () =>
                        handleInputChange({
                          target: { name: field.field_name, value: "" },
                        })
                }
              />
            )}
            showMonthDropdown
            showYearDropdown
            scrollableYearDropdown
            yearDropdownItemNumber={26}
            dropdownMode="scroll"
            placeholderText={`Enter ${field.title.toLowerCase()}`}
            customInput={
              <AnalysisDateInput
                placeholder={`Enter ${field.title.toLowerCase()}`}
              />
            }
            popperClassName="detail-datepicker-popper"
            popperPlacement="bottom-start"
            popperProps={{ strategy: "fixed" }}
            popperModifiers={CALENDAR_MODIFIERS}
            portalId="datepicker-portal"
            readOnly={currentMode === "view"}
            disabled={currentMode === "view"}
          />
        ) : isFileUploadField ? (
          currentMode === "view" ? (
            <div className="attachment-list attachment-list--view">
              {fieldAttachments.length ? (
                fieldAttachments.map((attachment) => {
                  const fileUrl =
                    attachment.url || attachment.file_url || attachment.s3_url;
                  const fileName =
                    attachment.file_name || attachment.name || "Download File";
                  return (
                    <div
                      className="attachment-list__item"
                      key={attachment.id || attachment.attachment_id}
                    >
                      <FileText size={16} />
                      <button
                        type="button"
                        className="link-button"
                        onClick={() => handleFileDownload(fileUrl, fileName)}
                      >
                        {fileName}
                      </button>
                    </div>
                  );
                })
              ) : (
                <span className="attachment-list__empty">
                  No files attached
                </span>
              )}
            </div>
          ) : (
            <div className="uploaded-image-containers drawing-upload-field">
              <input
                type="file"
                multiple
                ref={(el) =>
                  (dynamicFileInputRefs.current[field.field_name] = el)
                }
                onChange={(e) => handleDynamicFileChange(e, field.field_name)}
                className="custom-inputs"
                id={`dyn-file-input-${field.field_name}`}
              />
              <button
                type="button"
                className="custom-file-button"
                onClick={() =>
                  document
                    .getElementById(`dyn-file-input-${field.field_name}`)
                    ?.click()
                }
                style={{ cursor: "pointer" }}
              >
                Choose File
              </button>
              {fieldValue &&
                !selectedFileNames[field.field_name] &&
                !pendingFieldAttachments.length && (
                  <div className="uploaded-preview" style={{ marginRight: 8 }}>
                    <button
                      type="button"
                      className="link-button"
                      onClick={() =>
                        handleFileDownload(
                          fieldValue,
                          fieldValue.split("/").pop() || "download",
                        )
                      }
                    >
                      {fieldValue.split("/").pop() || "Uploaded File"}
                    </button>
                    <button
                      type="button"
                      className="remove-file-btn"
                      onClick={() => handleRemoveDynamicFile(field.field_name)}
                    >
                      <X size={16} />
                    </button>
                  </div>
                )}
              {(pendingFieldAttachments.length ||
                selectedFieldFileNames.length > 0) && (
                <div className="attachment-list attachment-list--pending">
                  {(pendingFieldAttachments.length
                    ? pendingFieldAttachments
                    : selectedFieldFileNames.map((fileName) => ({
                        file_name: fileName,
                      }))
                  ).map((attachment, index) => (
                    <div
                      className="attachment-list__item"
                      key={
                        attachment.id ||
                        attachment.attachment_id ||
                        `${attachment.file_name}-${index}`
                      }
                    >
                      <FileText size={16} />
                      <span
                        className="attachment-list__name"
                        title={attachment.file_name || attachment.name}
                      >
                        {attachment.file_name ||
                          attachment.name ||
                          "File uploaded"}
                      </span>
                      <button
                        type="button"
                        className="attachment-list__remove"
                        title="Remove this file"
                        onClick={() =>
                          handleRemoveMasterAttachment(
                            field.field_name,
                            attachment,
                          )
                        }
                      >
                        <X size={14} />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )
        ) : isMultiLineField ? (
          <textarea
            name={field.field_name}
            value={fieldValue || ""}
            onChange={(e) => {
              handleInputChange(e);
              if (currentMode !== "view") adjustHeight(e.target);
            }}
            className={`master-type-text-input ${
              currentMode === "view" ? "non-editable" : ""
            }`}
            disabled={currentMode === "view"}
            placeholder={`Enter ${field.title || field.field_name}`}
            ref={(el) => {
              inputRefs.current[field.field_name] = { current: el };
              if (el) adjustHeight(el);
            }}
            rows={1}
            maxLength={field.field_length}
            required={field.is_required}
          />
        ) : (
          <input
            type={
              isEmployeeMobileNumberField
                ? "tel"
                : field.data_type === "int"
                  ? "number"
                  : "text"
            }
            inputMode={isEmployeeMobileNumberField ? "numeric" : undefined}
            name={field.field_name}
            className={`master-data-modal-input ${
              currentMode === "view" ? "non-editable" : ""
            }`}
            placeholder={`Enter ${field.title.toLowerCase()}`}
            value={fieldValue}
            onChange={isTranNoField ? undefined : handleInputChange}
            readOnly={currentMode === "view" || isTranNoField}
            maxLength={field.field_length}
            required={field.is_required}
            disabled={isTranNoField && currentMode !== "view"}
          />
        )}
      </div>
    );
  };

  if (isLoading) {
    return (
      <div className="master-data-modal-container">
        <Loader />
      </div>
    );
  }

  return (
    <div className="master-data-modal-container">
      <div
        className="master-data-modal-header"
        style={{
          backgroundColor: colorCode ? `#${colorCode}` : "#fff",
        }}
      >
        <div className="master-data-modal-title-container">
          <h2 className="master-data-modal-title">
            {heading !== "otherDetailsModal"
              ? `${
                  isPopupMode ? "Quick Add " : ""
                }${capitalizeFirstLetterOfEachWord(
                  currentMode,
                )} ${capitalizeFirstLetterOfEachWord(displayCategory)}`
              : `${capitalizeFirstLetterOfEachWord(
                  modeOfEdit,
                )} ${capitalizeFirstLetterOfEachWord(selectedDetail.title)}`}
          </h2>
          {currentMode === "view" ? (
            <>
              <img
                src={Edit}
                onClick={toggleEditMode}
                className="sales-form-icons"
                alt="Edit"
                style={{
                  cursor: rights.can_edit ? "pointer" : "not-allowed",
                  opacity: rights.can_edit ? 1 : 0.5,
                }}
                title={rights.can_edit ? "Edit" : "No permission to edit"}
              />
              <img
                src={Delete}
                className="sales-form-icons"
                alt="Delete"
                onClick={handleDeleteClick}
                style={{
                  cursor: rights.can_delete ? "pointer" : "not-allowed",
                  opacity: rights.can_delete ? 1 : 0.5,
                  marginLeft: 12,
                }}
                title={
                  rights.can_delete
                    ? "Delete Transaction"
                    : "No permission to delete"
                }
              />
              {(transactionCode?.toUpperCase() === "VP" ||
                transactionCode?.toUpperCase() === "SEF" ||
                String(category || "")
                  .toLowerCase()
                  .includes("supplier-evaluation-form") ||
                isContractReviewMaster ||
                isTrainingAttendanceMaster ||
                isListOfEmployeesMaster ||
                isListOfTrainersMaster ||
                isHrInductionTrainingSheetMaster ||
                isHrTrainingNeedsRecordMaster ||
                isHrEmployeeHistoryCardMaster) && (
                <img
                  src={Print}
                  className="sales-form-icons"
                  alt="Print"
                  onClick={handlePrintClick}
                  style={{
                    cursor: "pointer",
                    opacity: 1,
                    marginLeft: 12,
                  }}
                  title="Print Transaction"
                />
              )}
              {onClose && (
                <button className="back-button-modal" onClick={onClose}>
                  <ArrowLeft size={25} />
                </button>
              )}
            </>
          ) : (onClose && currentMode === "add") || currentMode !== "add" ? (
            <X
              className="sales-form-icons cursor-pointer"
              onClick={() =>
                (modeOfEdit === "add" || currentMode === "add") && onClose
                  ? onClose()
                  : handleCloseEditMode()
              }
              size={24}
              title="Close Mode"
            />
          ) : null}
        </div>
      </div>

      <form onSubmit={handleSubmit} className="master-data-modal-form">
        <div
          className={
            heading !== "otherDetailsModal"
              ? "master-data-modal-form-fields"
              : "master-data-modal-field"
          }
        >
          {(() => {
            const normalFields = fields.filter(
              (f) => f.field_type !== "TEXT" && f.field_type !== "text",
            );
            const textFields = fields.filter(
              (f) => f.field_type === "TEXT" || f.field_type === "text",
            );
            return [...normalFields, ...textFields].map((field) =>
              renderField(field),
            );
          })()}
        </div>

        {isPopupMode && (
          <div className="popup-mode-indicator">
            <strong>Quick Add Mode:</strong> Only required fields are shown. For
            complete details, use the master menu.
          </div>
        )}

        {heading !== "otherDetailsModal" &&
          (extraForms || extraFormsFromFetch) &&
          (extraForms || extraFormsFromFetch).length > 0 && (
            <div className="add-extra-details">
              <h6>Other Details </h6>
              <div className="add-detail-buttons">
                {(extraForms || extraFormsFromFetch).map((form, index) => (
                  <button
                    key={index}
                    type="button"
                    className="add-detail-button"
                  >
                    {form.title}
                  </button>
                ))}
              </div>
            </div>
          )}

        <div className="master-data-modal-footer">
          {isButtonLoading && !showApproveConfirmation && !showRejectDialog ? (
            <div className="loader-container">
              <span className="loader"></span>
            </div>
          ) : (
            <>
              {currentMode === "view" && showMasterApprovalWorkflow && (
                <div className="approval-buttons-container">
                  {formData?.approve_status === "approved" ? (
                    <div className="status-approved">APPROVED</div>
                  ) : formData?.approve_status === "rejected" ? (
                    <div className="rejected-dropdown-container">
                      <div className="status-rejected">REJECTED</div>
                      <div className="view-reason-wrapper">
                        {showRejectReason && (
                          <div className="popup-tooltip">
                            <div className="popup-arrow" />
                            <div className="popup-detail">
                              {formData?.reject_reason || "Not specified"}
                            </div>
                          </div>
                        )}

                        <button
                          type="button"
                          className="reason-toggle-btn"
                          onClick={() => setShowRejectReason(!showRejectReason)}
                        >
                          {showRejectReason ? "Hide Reason" : "View Reason"}
                        </button>
                      </div>
                    </div>
                  ) : formData?.approve_status === "hold" ? (
                    <div className="rejected-dropdown-container">
                      <div className="status-hold">ON HOLD</div>

                      <div className="view-reason-wrapper">
                        {showRejectReason && (
                          <div className="popup-tooltip">
                            <div className="popup-arrow" />
                            <div className="popup-detail">
                              {formData?.reject_reason || "Not specified"}
                            </div>
                          </div>
                        )}

                        <button
                          type="button"
                          className="reason-toggle-btn"
                          onClick={() => setShowRejectReason(!showRejectReason)}
                        >
                          {showRejectReason ? "Hide Reason" : "View Reason"}
                        </button>
                      </div>
                    </div>
                  ) : (
                    <>
                      <button
                        className="approval-button approval-button--approve"
                        onClick={handleApprove}
                        disabled={isApprovalLoading}
                        type="button"
                      >
                        {isApprovalLoading ? "Processing..." : "APPROVE"}
                      </button>
                      <button
                        className="approval-button approval-button--hold"
                        onClick={handleHold}
                        disabled={isApprovalLoading}
                        type="button"
                      >
                        {isApprovalLoading ? "Processing..." : "HOLD"}
                      </button>
                      <button
                        className="approval-button approval-button--reject"
                        onClick={handleReject}
                        disabled={isApprovalLoading}
                        type="button"
                      >
                        {isApprovalLoading ? "Processing..." : "REJECT"}
                      </button>
                    </>
                  )}
                </div>
              )}
              {(currentMode === "add" ||
                currentMode === "edit" ||
                modeOfEdit === "add") && (
                <div className="master-data-modal-footer-actions">
                  <button
                    type="button"
                    className="master-data-modal-cancel-button"
                    onClick={() =>
                      modeOfEdit === "add" ||
                      (window.location.pathname.startsWith("/master") &&
                        currentMode === "add")
                        ? onClose()
                        : handleCloseEditMode()
                    }
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="master-data-modal-save-button"
                    style={{
                      cursor: (
                        currentMode === "add" ? rights.can_add : rights.can_edit
                      )
                        ? "pointer"
                        : "not-allowed",
                      opacity: (
                        currentMode === "add" ? rights.can_add : rights.can_edit
                      )
                        ? 1
                        : 0.5,
                    }}
                  >
                    Save
                  </button>
                  {currentMode === "edit" &&
                    showMasterApprovalWorkflow &&
                    (formData?.approve_status === "rejected" ||
                      formData?.approve_status === "hold") && (
                      <>
                        <button
                          type="button"
                          className="approval-button approval-button--approve"
                          onClick={handleApprove}
                          disabled={isApprovalLoading}
                        >
                          {isApprovalLoading ? "Processing..." : "APPROVE"}
                        </button>
                        {formData?.approve_status === "hold" && (
                          <button
                            type="button"
                            className="approval-button approval-button--reject"
                            onClick={handleReject}
                            disabled={isApprovalLoading}
                          >
                            {isApprovalLoading ? "Processing..." : "REJECT"}
                          </button>
                        )}
                      </>
                    )}
                </div>
              )}
            </>
          )}
        </div>
      </form>

      {showProcessCardPopup && (
        <ProcessCardPopup
          processCardNo={formData?.tran_id || ""}
          productName={formData?.name || ""}
          productDescription={formData?.description || ""}
          onClose={() => setShowProcessCardPopup(false)}
          onProcessCardSaved={handleProcessCardSaved}
          process_card_created={formData?.process_card_created === true}
          process_card_id={formData?.process_card_id}
        />
      )}

      <ConfirmationModal
        isOpen={showDeleteConfirmation}
        onClose={() => setShowDeleteConfirmation(false)}
        onConfirm={handleDeleteConfirm}
        message="Are you sure you want to delete this transaction?"
        confirmText="Delete"
        cancelText="Cancel"
      />

      {showRejectDialog && (
        <div
          className="modal-overlay"
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              setShowRejectDialog(false);
              setRejectReason("");
              setIsApprovalLoading(false);
              setIsButtonLoading(false);
              setActionType("");
            }
          }}
        >
          <div className="modal-content">
            <p className="modal-message">
              {actionType === "hold"
                ? "Enter reason for hold"
                : "Enter reason for rejection"}
            </p>

            <textarea
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              className="modal-textarea"
              placeholder="Enter reason"
              required
            />

            <div className="modal-buttons">
              <button
                className="modal-button confirm-button reject-button"
                onClick={handleConfirmRejectOrHold}
                disabled={!rejectReason.trim() || isApprovalLoading}
                style={{
                  backgroundColor:
                    !rejectReason.trim() || isApprovalLoading
                      ? undefined
                      : "#e03a0d",
                  cursor:
                    !rejectReason.trim() || isApprovalLoading
                      ? "not-allowed"
                      : "pointer",
                }}
              >
                {isApprovalLoading
                  ? "Processing..."
                  : actionType === "hold"
                    ? "Hold"
                    : "Reject"}
              </button>

              <button
                className="modal-button cancel-button"
                onClick={() => {
                  setShowRejectDialog(false);
                  setRejectReason("");
                  setIsApprovalLoading(false);
                  setIsButtonLoading(false);
                  setActionType("");
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      <ConfirmationModal
        isOpen={showApproveConfirmation}
        onClose={() => {
          setShowApproveConfirmation(false);
          setIsApprovalLoading(false);
          setIsButtonLoading(false);
        }}
        onConfirm={handleConfirmApprove}
        message="Are you sure you want to approve this transaction?"
        confirmText={isApprovalLoading ? "Processing..." : "Approve"}
        cancelText="Cancel"
      />
    </div>
  );
};

export default DrawingStatusMaster;
